import { useState, useEffect } from 'react';
import { Activity, Brain, Radio, BarChart3, GitMerge, Map, DollarSign, TrendingUp, Shield, Cpu, Wallet, Split, Waves, Globe, Bell, Clock, Database, Send, Users, Landmark, ChevronDown, ChevronRight, ListChecks, Settings as SettingsIcon, Zap, Layers, Bitcoin, Hammer, Lock, Menu, X } from 'lucide-react';

const SIDEBAR_GROUPS = [
  {
    label: 'COMMAND', alwaysOpen: true,
    modules: [
      { name: 'Portfolio Command', icon: Brain, path: '/portfolio-command', sprint: 2 },
      { name: 'Holdings', icon: Lock, path: '/holdings', sprint: 21.6, locked: true },
      { name: 'Watchlist', icon: ListChecks, path: '/watchlist', sprint: 13 },
      { name: 'Alerts', icon: Bell, path: '/alerts', sprint: 1 },
    ],
  },
  // Sprint 21.7.2: Domain Command Centers (ADR-027). Each Command Center
  // is a specialist's UI home. Phase 2 sprints add more (options, fx, etc).
  {
    label: 'COMMAND CENTERS',
    modules: [
      { name: 'Equity',        icon: Landmark, path: '/command/equity',        sprint: 21.7 },
      { name: 'Fixed Income',  icon: Hammer,   path: '/command/fixed-income',  sprint: 21.7 },
      { name: 'Crypto',        icon: Bitcoin,  path: '/command/crypto',        sprint: 21.7 },
      { name: 'Macro',         icon: Globe,    path: '/command/macro',         sprint: 21.7 },
      // Sprint 21.11: /options promoted from stub to JANUS's Command Center
      { name: 'Options & Vol', icon: Activity, path: '/command/options',       sprint: 21.11 },
    ],
  },
  {
    label: 'INTELLIGENCE',
    modules: [
      { name: 'Macro Radar', icon: Radio, path: '/macro-radar', sprint: 1 },
      { name: 'Themes', icon: Layers, path: '/themes', sprint: 17 },
      // Sprint 21.7.2: "Fixed Income" standalone removed; replaced by /command/fixed-income.
      // Legacy FixedIncomePage still accessible at /fixed-income/legacy if needed.
      { name: 'Credibility', icon: Shield, path: '/credibility', sprint: 16 },
      { name: 'Pantheon', icon: Users, path: '/agents', sprint: 21 },
      { name: 'Telegram Feed', icon: Send, path: '/telegram', sprint: 9 },
      { name: 'Voices', icon: Users, path: '/voices', sprint: 10 },
      { name: 'Smart Money', icon: Landmark, path: '/smart-money', sprint: 12 },
      // Sprint 21.16: GEO INTEL HUB (ADR-057) — link/embed external geopolitical
      // tools + ACLED/RSS ingestion. Geopolitical specialist (Phase 2 #5/12)
      // ships separately in 21.17 once data layer accumulates.
      { name: 'Geo Intel', icon: Globe, path: '/geo-intel', sprint: 21.16 },
      // Sprint 21.17 ATHENA — per-module track record + convergence + devil's
      // advocate audits. Forward-only from this sprint per ADR-059.
      { name: 'Module Performance', icon: Brain, path: '/module-performance', sprint: 21.17 },
      // Sprint 22.7.10 (S-B): per-domain KAIROS prediction performance. sprint
      // field is a visibility gate (float, ≤ MAX_SPRINT=21.8), NOT a ship stamp;
      // 21.8 keeps the link live without bumping MAX_SPRINT (real sprint 22.7.10).
      { name: 'KAIROS Performance', icon: BarChart3, path: '/kairos-performance', sprint: 21.8 },
    ],
  },
  {
    label: 'ANALYSIS',
    modules: [
      { name: 'Signal Convergence', icon: GitMerge, path: '/signals', sprint: 2 },
      { name: 'Scenario Engine', icon: Map, path: '/scenarios', sprint: 5 },
      { name: 'Butterfly Engine', icon: Waves, path: '/butterfly', sprint: 4 },
      { name: 'Geo Translator', icon: Globe, path: '/geo', sprint: 5 },
    ],
  },
  {
    label: 'MARKETS',
    modules: [
      { name: 'Currency Intel', icon: DollarSign, path: '/currency', sprint: 2 },
      { name: 'Crypto Pulse', icon: Bitcoin, path: '/crypto', sprint: 18 },
      // Sprint 21.11: /options legacy entry retired. JANUS's Command Center
      // at /command/options (under COMMAND CENTERS) is the live home.
      { name: 'Narrative Divergence', icon: Split, path: '/narrative', sprint: 4 },
    ],
  },
  {
    label: 'RISK & TRACKING',
    modules: [
      { name: 'Risk Engine', icon: Shield, path: '/risk', sprint: 4 },
      { name: 'Wealth Command', icon: Wallet, path: '/wealth', sprint: 8 },
      { name: 'Futures Signals', icon: BarChart3, path: '/futures', sprint: 1 },
    ],
  },
  {
    label: 'MEMORY',
    modules: [
      { name: 'Mnemosyne', icon: Database, path: '/mnemosyne', sprint: 2 },
      { name: 'History', icon: Clock, path: '/history', sprint: 2 },
    ],
  },
  {
    label: 'SYSTEM',
    modules: [
      // Sprint 22.7.14 (Tier 1 build): Operations dashboard. sprint field is a
      // visibility gate (float, <= MAX_SPRINT=21.8), NOT a ship stamp; 21.8 keeps
      // the link live without bumping MAX_SPRINT (real sprint 22.7.14).
      { name: 'Operations', icon: Activity, path: '/operations', sprint: 21.8 },
      // Sprint 21.7.6 Issue 13: restore /system/costs (Sprint 3.5 infra; operator-tier)
      { name: 'Costs', icon: DollarSign, path: '/system/costs', sprint: 3.5 },
      { name: 'Tech Scout', icon: Cpu, path: '/scout', sprint: 9 },
      { name: 'Settings', icon: SettingsIcon, path: '/settings', sprint: 14 },
      { name: 'Signal Queue', icon: Zap, path: '/signal-queue', sprint: 16 },
      // Sprint 21.18.1 (ADR-065): Polymarket curation UI — server-side
      // Gamma discovery + tracked_markets management. Hotfix that
      // completes the Sprint 21.15 data layer.
      { name: 'Polymarket Curate', icon: Bitcoin, path: '/admin/polymarket-curate', sprint: 21.18 },
    ],
  },
];

const MAX_SPRINT = 21.8;

function loadCollapsed() {
  try { return JSON.parse(localStorage.getItem('korinth_sidebar_collapsed') || '{}'); } catch { return {}; }
}

function SidebarInner({ onNavigate }) {
  const currentPath = window.location.pathname;
  const [ver, setVer] = useState({ version: '...', sprint: '...' });
  const [collapsed, setCollapsed] = useState(() => loadCollapsed());
  const [coreCount, setCoreCount] = useState(null);
  const [unreadAlerts, setUnreadAlerts] = useState(0);

  useEffect(() => {
    fetch('/api/health').then(r => r.json()).then(d => setVer(d)).catch(() => {});
    async function loadCore() {
      try {
        const r = await fetch('/api/watchlist/user');
        if (r.ok) { const j = await r.json(); setCoreCount(j.summary?.core_count ?? 0); }
      } catch {}
    }
    async function loadUnread() {
      try {
        const lastRead = localStorage.getItem('korinth_alerts_last_read') || new Date(0).toISOString();
        const r = await fetch(`/api/alerts/unread-count?since=${encodeURIComponent(lastRead)}`);
        if (r.ok) { const d = await r.json(); setUnreadAlerts(d.unread || 0); }
      } catch {}
    }
    loadCore();
    loadUnread();
    const iv = setInterval(() => { loadCore(); loadUnread(); }, 60000);
    return () => clearInterval(iv);
  }, []);

  useEffect(() => {
    for (const group of SIDEBAR_GROUPS) {
      if (group.modules.some(m => m.path === currentPath || (currentPath === '/' && m.path === '/portfolio-command'))) {
        if (collapsed[group.label]) {
          const next = { ...collapsed, [group.label]: false };
          setCollapsed(next);
          localStorage.setItem('korinth_sidebar_collapsed', JSON.stringify(next));
        }
        break;
      }
    }
  }, [currentPath]);

  function toggleGroup(label) {
    const next = { ...collapsed, [label]: !collapsed[label] };
    setCollapsed(next);
    localStorage.setItem('korinth_sidebar_collapsed', JSON.stringify(next));
  }

  return (
    <>
      <div className="p-4 border-b border-korinth-border">
        <div className="flex items-center gap-2">
          <Activity className="w-6 h-6 text-korinth-accent" />
          <span className="text-lg font-bold text-white font-mono tracking-wider">KORINTH</span>
        </div>
        <p className="text-xs text-korinth-muted mt-1">Strategic Intelligence OS</p>
      </div>

      <nav className="flex-1 overflow-y-auto py-1">
        {SIDEBAR_GROUPS.map((group) => {
          const isOpen = group.alwaysOpen || !collapsed[group.label];
          const hasActivePage = group.modules.some(m => m.path === currentPath || (currentPath === '/' && m.path === '/portfolio-command'));
          return (
            <div key={group.label} className="mb-0.5">
              {!group.alwaysOpen ? (
                <button
                  onClick={() => toggleGroup(group.label)}
                  className={`flex items-center gap-1.5 w-full px-4 py-1.5 text-[9px] font-mono uppercase tracking-widest transition-colors ${hasActivePage ? 'text-korinth-accent' : 'text-korinth-muted hover:text-korinth-text'}`}
                >
                  {isOpen ? <ChevronDown className="w-2.5 h-2.5" /> : <ChevronRight className="w-2.5 h-2.5" />}
                  {group.label}
                </button>
              ) : (
                <div className="px-4 py-1.5 text-[9px] font-mono uppercase tracking-widest text-korinth-accent">
                  {group.label}
                </div>
              )}

              {isOpen && group.modules.map((mod) => {
                const Icon = mod.icon;
                const isActive = currentPath === mod.path || (currentPath === '/' && mod.path === '/portfolio-command');
                const isAvailable = mod.sprint <= MAX_SPRINT;
                return (
                  <a
                    key={mod.path}
                    href={mod.path}
                    onClick={(e) => {
                      if (!isAvailable) { e.preventDefault(); return; }
                      if (onNavigate) onNavigate();
                    }}
                    // 44x44 min tappable target on mobile (iOS HIG)
                    className={`flex items-center gap-2.5 px-5 py-2 md:py-1.5 text-[13px] transition-colors ${
                      isActive
                        ? 'bg-korinth-accent/10 text-korinth-accent border-r-2 border-korinth-accent'
                        : isAvailable
                          ? 'text-korinth-text hover:bg-korinth-border/50 active:bg-korinth-border/70'
                          : 'text-korinth-muted/50 cursor-not-allowed'
                    }`}
                  >
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                    <span>{mod.name}</span>
                    {mod.path === '/watchlist' && coreCount != null && coreCount > 0 && (
                      <span className="ml-auto text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-korinth-accent/15 text-korinth-accent">{coreCount}</span>
                    )}
                    {mod.path === '/alerts' && unreadAlerts > 0 && (
                      <span className="ml-auto text-[9px] font-mono font-bold px-1.5 py-0.5 rounded bg-korinth-accent text-black">{unreadAlerts > 99 ? '99+' : unreadAlerts}</span>
                    )}
                    {!isAvailable && (
                      <span className="ml-auto text-[9px] text-korinth-muted/40">S{mod.sprint}</span>
                    )}
                  </a>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="p-4 border-t border-korinth-border">
        <p className="text-[10px] text-korinth-muted font-mono">v{ver.version} — Sprint {ver.sprint}</p>
      </div>
    </>
  );
}

/**
 * Sprint 21.7.2: Sidebar now has dual-mode rendering:
 *   - ≥ lg (1024px): fixed left sidebar, always visible
 *   - < lg: hidden by default; hamburger in top-left of viewport toggles a
 *     slide-in drawer with full-screen overlay behind it
 *
 * Same component, just wrapped for mobile.
 */
export default function Sidebar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close drawer when viewport grows past lg
  useEffect(() => {
    function onResize() { if (window.innerWidth >= 1024) setMobileOpen(false); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Lock body scroll when mobile drawer open
  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <>
      {/* Mobile top bar with hamburger — only visible below lg */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-12 z-40 bg-korinth-surface border-b border-korinth-border flex items-center gap-2 px-3">
        <button
          onClick={() => setMobileOpen(true)}
          aria-label="Open navigation"
          className="w-11 h-11 flex items-center justify-center text-korinth-text hover:text-korinth-accent -ml-2"
        >
          <Menu className="w-5 h-5" />
        </button>
        <Activity className="w-5 h-5 text-korinth-accent" />
        <span className="text-sm font-bold text-white font-mono tracking-wider">KORINTH</span>
      </div>

      {/* Mobile drawer overlay */}
      {mobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40 bg-black/60"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Mobile drawer (slides in from left when mobileOpen) */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 z-50 w-72 max-w-[85vw] bg-korinth-surface border-r border-korinth-border flex flex-col transform transition-transform duration-200 ease-out ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="flex items-center justify-end p-2 border-b border-korinth-border lg:hidden">
          <button
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
            className="w-11 h-11 flex items-center justify-center text-korinth-muted hover:text-korinth-text"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <SidebarInner onNavigate={() => setMobileOpen(false)} />
      </aside>

      {/* Desktop fixed sidebar (≥ lg) */}
      <aside className="hidden lg:flex w-56 bg-korinth-surface border-r border-korinth-border flex-col">
        <SidebarInner />
      </aside>
    </>
  );
}
